/**
 * Der Sync-Motor: senden, holen, zusammenführen.
 *
 * Ablauf einer Runde ist bewusst **erst senden, dann holen**. Andersherum würde
 * eine gerade lokal geänderte Zeile vom Server-Stand überschrieben, bevor die
 * eigene Änderung dort überhaupt bekannt ist — und der Nutzer sähe seine Eingabe
 * verschwinden.
 */

import {
  E2E_REQUIRED_REASON,
  HOUSEHOLD_TABLES,
  RECORD_TABLES,
  incomingWins,
  isEncryptedTable,
  PUSH_BATCH_SIZE,
  SHAREABLE_TABLES,
  SYNC_TABLES,
} from '@kraftwerk/shared';
import type { PullResponse, PushResponse, SyncedRow } from '@kraftwerk/shared';
import { ApiError, apiFetch } from '../api.js';
import {
  db,
  readCursors,
  readHouseholdIds,
  resetCursors,
  writeCursor,
  writeHouseholdIds,
} from '../db/local.js';
import type { KraftwerkDatabase, OutboxEntry } from '../db/local.js';
import { writeEncryptedHouseholds, writeEncryptionSetup } from '../crypto/e2e.js';
import { acceptPulled, prepareForPush, requeueRows } from './e2e.js';

/** Ab so vielen Fehlversuchen wird nicht weiter gesendet, aber auch nichts gelöscht. */
export const MAX_ATTEMPTS = 5;

export interface SyncResult {
  pushed: number;
  pulled: number;
  /** Vom Server begründet abgelehnt — gehört in die Oberfläche, nicht ins Nichts. */
  rejected: { mutationId: string; reason: string }[];
  /** Netz- oder Serverfehler. Kein Grund, irgendetwas zu verwerfen. */
  error?: string;
  /**
   * Was für ein Fehler — damit die Oberfläche das Richtige sagt:
   * `unreachable` (kein Netz, Server oder Tunnel weg), `unauthorized` (Sitzung
   * abgelaufen — neu anmelden, nichts verwerfen), `server` (der Server hat
   * geantwortet, aber mit einem Fehler), `update` (diese Fassung ist zu alt —
   * neu laden, nichts verwerfen).
   */
  errorKind?: SyncErrorKind;
  status?: number;
}

export type SyncErrorKind = 'unreachable' | 'unauthorized' | 'server' | 'update';

/** Antworten eines Proxys, hinter dem der Server nicht erreichbar ist. */
const GATEWAY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 530]);

export function classifySyncError(error: unknown): { kind: SyncErrorKind; status?: number } {
  if (error instanceof ApiError) {
    if (error.status === 401) return { kind: 'unauthorized', status: 401 };
    if (error.status === 426) return { kind: 'update', status: 426 };
    if (GATEWAY_STATUSES.has(error.status)) return { kind: 'unreachable', status: error.status };
    return { kind: 'server', status: error.status };
  }
  // fetch wirft ohne Antwort (kein Netz, DNS, abgebrochen).
  return { kind: 'unreachable' };
}

export interface SyncDeps {
  database?: KraftwerkDatabase;
  post?: <T>(path: string, body: unknown) => Promise<T>;
}

/**
 * Längste Wartezeit je Anfrage. Ohne Grenze hing eine Anfrage, die iOS beim
 * Einfrieren der App mitten im Senden erwischt hatte, nach dem Aufwachen
 * weiter — und weil Runden nicht überlappen, wartete jede folgende mit. Dann
 * kam die Erinnerung zum Antippen nicht an, und die App öffnete ohne
 * „Genommen?“ (am iPhone, „nicht immer“, 23.10.2026). Geholt wird seitenweise;
 * eine gesunde Anfrage braucht Sekundenbruchteile.
 */
export const SYNC_REQUEST_TIMEOUT_MS = 15_000;

async function defaultPost<T>(path: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_REQUEST_TIMEOUT_MS);
  try {
    return await apiFetch<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function pendingCount(database: KraftwerkDatabase = db): Promise<number> {
  return database.outbox.count();
}

export async function failedEntries(database: KraftwerkDatabase = db): Promise<OutboxEntry[]> {
  const all = await database.outbox.toArray();
  return all.filter((entry) => entry.attempts >= MAX_ATTEMPTS);
}

/**
 * Abgelehnte Änderungen erneut zum Senden freigeben.
 *
 * Ohne das ist „abgelehnt" eine Sackgasse: Der Motor hört nach `MAX_ATTEMPTS`
 * auf, und selbst wenn die Ursache längst behoben ist — ein Serverfehler, eine
 * reparierte Fassung — passiert nie wieder etwas. Sichtbar, aber ohne Ausweg,
 * ist nur die halbe Miete.
 *
 * Aufgefallen beim Test auf dem echten iPhone.
 */
export async function retryFailed(database: KraftwerkDatabase = db): Promise<number> {
  const stuck = await failedEntries(database);
  await database.transaction('rw', database.outbox, async () => {
    for (const entry of stuck) {
      if (entry.seq === undefined) continue;
      // `lastError` wird gelöscht statt auf undefined gesetzt — Dexie
      // unterscheidet das, und ein liegengebliebener alter Grund wäre irreführend.
      const { lastError: _verworfen, ...rest } = entry;
      await database.outbox.put({ ...rest, attempts: 0 });
    }
  });
  return stuck.length;
}

async function pushOutbox(
  database: KraftwerkDatabase,
  post: NonNullable<SyncDeps['post']>,
): Promise<{ pushed: number; rejected: SyncResult['rejected'] }> {
  const all = await database.outbox.orderBy('seq').toArray();
  // Verschlüsselte Tabellen gehen als Chiffrat hinaus; ohne Schlüssel bleiben
  // sie in der Outbox (E21).
  const prepared = (
    await prepareForPush(
      all.filter((entry) => entry.attempts < MAX_ATTEMPTS),
      database,
    )
  ).slice(0, PUSH_BATCH_SIZE);
  const sendable = prepared.map(({ entry }) => entry);
  if (sendable.length === 0) return { pushed: 0, rejected: [] };

  const response = await post<PushResponse>('/sync/push', {
    mutations: prepared.map(({ entry, row }) => ({
      mutationId: entry.mutationId,
      table: entry.table,
      kind: entry.kind,
      row,
    })),
  });

  const appliedIds = new Set(response.applied);
  const rejectedById = new Map(response.rejected.map((entry) => [entry.mutationId, entry.reason]));

  await database.transaction('rw', database.outbox, async () => {
    for (const entry of sendable) {
      if (appliedIds.has(entry.mutationId)) {
        if (entry.seq !== undefined) await database.outbox.delete(entry.seq);
        continue;
      }

      const reason = rejectedById.get(entry.mutationId);
      // Das Konto verschlüsselt inzwischen (anderes Gerät): kein Fehlversuch —
      // die Änderung wartet, bis dieses Gerät entsperrt ist.
      if (reason === E2E_REQUIRED_REASON) continue;
      if (reason && entry.seq !== undefined) {
        // Abgelehntes wird **nicht** gelöscht. Es bleibt sichtbar, bis jemand
        // entscheidet, was damit geschehen soll.
        await database.outbox.update(entry.seq, {
          attempts: entry.attempts + 1,
          lastError: reason,
        });
      }
    }
  });

  if (response.rejected.some((entry) => entry.reason === E2E_REQUIRED_REASON)) {
    await writeEncryptionSetup(true, database);
  }
  return {
    pushed: appliedIds.size,
    rejected: response.rejected.filter((entry) => entry.reason !== E2E_REQUIRED_REASON),
  };
}

async function pullChanges(
  database: KraftwerkDatabase,
  post: NonNullable<SyncDeps['post']>,
): Promise<number> {
  let applied = 0;
  // Obergrenze gegen eine Endlosschleife, falls der Server dauerhaft `hasMore`
  // meldet, ohne dass der Cursor vorankommt.
  for (let round = 0; round < 20; round += 1) {
    const cursors = await readCursors(database);
    const response = await post<PullResponse>('/sync/pull', { cursors });
    if (typeof response.encryption === 'boolean') {
      await writeEncryptionSetup(response.encryption, database);
    }
    if (Array.isArray(response.encryptedHouseholds)) {
      await writeEncryptedHouseholds(response.encryptedHouseholds, database);
    }

    for (const change of response.changes) {
      if (!(SYNC_TABLES as readonly string[]).includes(change.table)) continue;
      const target = database.tableOf(change.table);
      // Entschlüsseln vor der Transaktion; Verschlossenes wartet (E21).
      const { rows, reseal } = await acceptPulled(change.table, change.rows, database);

      await database.transaction('rw', target, async () => {
        for (const row of rows) {
          const existing = (await target.get(row.id)) as SyncedRow | undefined;
          // Konfliktregel: die neuere Änderung gewinnt. Eine lokal geänderte,
          // noch nicht gesendete Zeile ist neuer und bleibt deshalb stehen.
          if (incomingWins(row, existing)) {
            await target.put(row as never);
            applied += 1;
          }
        }
      });

      // Mit einem alten Schlüssel geöffnet (Haushalt umgezogen): mit dem
      // richtigen neu senden, damit die anderen Mitglieder es lesen können.
      // Ebenso Klartext beim Server im verschlüsselten Konto (Vorlagen).
      if (reseal.length > 0 && isEncryptedTable(change.table)) {
        await requeueRows(database, change.table, reseal);
      }

      if (change.rows.length > 0) await writeCursor(change.table, change.cursor, database);
    }

    // Haushalt gewechselt: Die neuen Zeilen liegen zeitlich vor dem Cursor und
    // kämen sonst nie an — also gleich noch eine Runde, von vorn.
    if (await reconcileHouseholds(database, response.householdIds)) continue;

    if (!response.hasMore) break;
  }

  return applied;
}

/**
 * Mitgliedschaft abgleichen. Gibt `true` zurück, wenn neu geholt werden muss.
 *
 * Zwei Dinge kann der Cursor nicht: Zeilen eines Haushalts liefern, dem man
 * gerade beigetreten ist (sie sind älter als der Cursor), und Zeilen eines
 * verlassenen Haushalts löschen (der Server zeigt sie einem nicht mehr). Beides
 * passiert deshalb hier, ausgelöst durch die Liste der Haushalte, die der
 * Server bei jedem Holen mitschickt.
 *
 * Nicht gesendete Änderungen an einem verlassenen Haushalt bleiben in der
 * Outbox: Der Server lehnt sie ab, und die Ablehnung ist sichtbar. Sie still zu
 * verwerfen, wäre genau der Datenverlust, gegen den die Outbox gebaut ist.
 */
async function reconcileHouseholds(
  database: KraftwerkDatabase,
  householdIds: readonly string[] | undefined,
): Promise<boolean> {
  if (!Array.isArray(householdIds)) return false;

  const next = [...householdIds].sort();
  const previous = await readHouseholdIds(database);
  await writeHouseholdIds(next, database);

  // Beim allerersten Abgleich gibt es nichts zu wechseln.
  if (previous === null) return false;
  if (previous.length === next.length && previous.every((id, index) => id === next[index])) {
    return false;
  }

  const keep = new Set(next);
  await database.transaction(
    'rw',
    [
      database.households,
      database.household_members,
      database.shopping_items,
      database.pantry_items,
      database.household_events,
      database.task_lists,
      database.tasks,
      database.task_completions,
      database.pocket_money,
      database.pocket_goals,
      database.star_entries,
      database.places,
      database.location_checkins,
      database.record_entries,
      database.household_meals,
      database.household_meal_eaters,
    ],
    async () => {
      await database.households.filter((row) => !keep.has(row.id)).delete();
      for (const table of [
        database.household_members,
        database.shopping_items,
        database.pantry_items,
        database.household_events,
        // Taschengeld (E20) steht im Haushalt des Kindes — wer geht, sieht es nicht mehr.
        database.pocket_money,
        database.pocket_goals,
        database.star_entries,
        // Standort (27.10.2026): Orte und Check-ins des verlassenen Haushalts.
        database.places,
        database.location_checkins,
        // Haushalts-Essensplan (27.10.2026). Eigene Abweichungen bleiben —
        // ohne Eintrag zeigt sie nichts, und sie gehören dem Konto.
        database.household_meals,
        database.household_meal_eaters,
      ]) {
        await table.filter((row) => !keep.has(row.householdId)).delete();
      }
      // Teilbare Zeilen: nur die geteilten eines verlassenen Haushalts — private bleiben.
      // Akte (27.10.2026): die eines Kindes im verlassenen Haushalt, nie die eigene.
      for (const table of [
        database.task_lists,
        database.tasks,
        database.task_completions,
        database.record_entries,
      ]) {
        await table
          .filter((row) => row.householdId !== null && !keep.has(row.householdId))
          .delete();
      }
    },
  );
  await resetCursors([...HOUSEHOLD_TABLES, ...SHAREABLE_TABLES, ...RECORD_TABLES], database);
  return true;
}

/**
 * Eine vollständige Runde. Wirft nicht — ein Netzfehler ist ein normaler Zustand
 * und wird als Ergebnis zurückgegeben, damit die Oberfläche ihn zeigen kann.
 */
export async function syncOnce(deps: SyncDeps = {}): Promise<SyncResult> {
  const database = deps.database ?? db;
  const post = deps.post ?? defaultPost;

  try {
    const { pushed, rejected } = await pushOutbox(database, post);
    const pulled = await pullChanges(database, post);
    return { pushed, pulled, rejected };
  } catch (error) {
    const { kind, status } = classifySyncError(error);
    return {
      pushed: 0,
      pulled: 0,
      rejected: [],
      error: error instanceof Error ? error.message : 'Synchronisation fehlgeschlagen',
      errorKind: kind,
      ...(status === undefined ? {} : { status }),
    };
  }
}
